import classNames from 'classnames';
import React from 'react';
import type { ExtendWithCommonProps } from '../../types';
import { Flex } from '../flex/flex';
import { Layout, type LayoutProps } from '../layout/layout';
import { Text } from '../text/text';
import { TagButton } from './tag-button';
import { TagClose } from './tag-close';
import { TagProvider } from './tag-context';
import { TagIcon } from './tag-icon';
import scss from './tag.module.scss';

export const tagVariants = [
  'primary',
  'accent',
  'light',
  'glass',
  'brabus',
  'inverted',
] as const;
export type TagVariants = (typeof tagVariants)[number];

export type TagProps = ExtendWithCommonProps<
  {
    /** Tag's appearance variant. */
    variant?: TagVariants;
    /** If true, notification is open. */
    open?: boolean;
    /** Callback when open state changes. */
    onOpenChange?: (open: boolean) => void;
    /** Button's content. */
    children: React.ReactNode;
    /** If true, button is disabled. */
    disabled?: boolean;
  } & Pick<LayoutProps, 'display' | 'padding' | 'margin'>
>;

const Tag = React.forwardRef<HTMLDivElement, TagProps>(
  (
    {
      variant = 'primary',
      open,
      onOpenChange,
      disabled,
      children,
      display,
      padding,
      margin,
      className,
      ...props
    }: TagProps,
    ref,
  ) => {
    const controlled = open !== undefined;
    const [controlledOpen, setControlledOpen] = React.useState(
      controlled ? open : true,
    );

    const classes = classNames(
      scss.button,
      scss[variant],
      {
        [scss.disabled]: disabled,
      },
      className,
    );

    if ((controlled && !open) || (!controlled && !controlledOpen)) return null;

    return (
      <TagProvider
        open={controlledOpen}
        onOpenChange={onOpenChange}
        disabled={disabled}
        controlled={controlled}
        setControlledOpen={setControlledOpen}
      >
        <Text className={scss.label} variant="cap-300" asChild>
          <Layout
            display={display}
            padding={padding}
            margin={margin}
            borderRadius={300}
            width={'fit-content'}
            className={classes}
            asChild
            {...props}
          >
            <div ref={ref}>
              <Flex
                justifyContent="center"
                alignItems="center"
                padding={{ x: 100, y: 50 }}
                gap={50}
                width="full"
                className={scss.content}
              >
                {children}
              </Flex>
            </div>
          </Layout>
        </Text>
      </TagProvider>
    );
  },
);

/**
 * Composed of:
 * - `<Tag.Icon>` to add an icon to the tag.
 * - `<Tag.Button>` to add a button to the tag.
 * - `<Tag.Close>` to add a close button to the tag, can be applied to other element with `asChild`.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-tag--docs
 */
const TagNamespace = Object.assign(Tag, {
  Close: TagClose,
  Button: TagButton,
  Icon: TagIcon,
});

export { TagNamespace as Tag };
Tag.displayName = 'Tag';
